import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { onboardingApi } from '@/lib/api';

const schema = z.object({
  hospitalName:     z.string().min(2, 'Required'),
  addressLine:      z.string().min(5, 'Enter full address'),
  pinCode:          z.string().regex(/^\d{6}$/, '6-digit PIN required'),
  city:             z.string().min(2, 'Required'),
  district:         z.string().min(2, 'Required'),
  state:            z.string().min(2, 'Required'),
  contactEmail:     z.string().email('Invalid email'),
  contactMobile:    z.string().regex(/^\d{10}$/, '10-digit number'),
  gstin:            z.string().regex(/^[0-9A-Z]{15}$/, 'Invalid GSTIN').optional().or(z.literal('')),
  regNumber:        z.string().optional(),
  bedCountCategory: z.enum(['5-10', '11-30', '31-50']),
  specialtyType:    z.string().min(1, 'Select specialty'),
});
type FormData = z.infer<typeof schema>;

const SPECIALTIES = [
  'Multispecialty', 'Maternity & Gynecology', 'ENT', 'Orthopaedic',
  'Paediatric', 'General Surgery', 'Dermatology & Cosmetology',
  'Eye / Ophthalmology', 'Dental', 'Psychiatry', 'Other',
];

interface Props {
  tenantId: string;
  token: string;
  onNext: (tenantSlug: string) => void;
}

export default function Step1BasicInfo({ tenantId, token, onNext }: Props) {
  const [pinLoading, setPinLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { bedCountCategory: '5-10', specialtyType: 'Multispecialty' },
  });

  const lookupPin = useCallback(async (pin: string) => {
    if (!/^\d{6}$/.test(pin)) return;
    setPinLoading(true);
    try {
      const { data } = await onboardingApi.pincode(pin);
      setValue('district', data.district, { shouldValidate: true });
      setValue('state',    data.state,    { shouldValidate: true });
    } catch {
      // user fills manually
    } finally {
      setPinLoading(false);
    }
  }, [setValue]);

  async function onSubmit(values: FormData) {
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await onboardingApi.step1(tenantId, token, values);
      onNext(data.tenantSlug);
    } catch (e: any) {
      setError(e.response?.data?.error ?? 'Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const field = 'w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange disabled:bg-gray-50 disabled:text-gray-400';
  const label = 'block text-sm font-medium text-navy mb-1.5';
  const err   = 'text-clinical-danger text-xs mt-1';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Hospital name */}
      <div>
        <label className={label}>Hospital / Clinic Name *</label>
        <input {...register('hospitalName')} placeholder="City Hospital & Research Centre" className={field}/>
        {errors.hospitalName && <p className={err}>{errors.hospitalName.message}</p>}
      </div>

      {/* Address */}
      <div>
        <label className={label}>Address *</label>
        <input {...register('addressLine')} placeholder="Plot 12, MG Road, Near Railway Station" className={field}/>
        {errors.addressLine && <p className={err}>{errors.addressLine.message}</p>}
      </div>

      {/* PIN + city */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={label}>PIN Code *</label>
          <div className="relative">
            <input
              {...register('pinCode')}
              placeholder="440001"
              maxLength={6}
              className={field}
              onBlur={e => lookupPin(e.target.value)}
            />
            {pinLoading && (
              <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400"/>
            )}
          </div>
          {errors.pinCode && <p className={err}>{errors.pinCode.message}</p>}
        </div>
        <div>
          <label className={label}>City *</label>
          <input {...register('city')} placeholder="Nagpur" className={field}/>
          {errors.city && <p className={err}>{errors.city.message}</p>}
        </div>
      </div>

      {/* District + state (auto-filled) */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={label}>District *</label>
          <input {...register('district')} placeholder="Auto-filled from PIN" className={field}/>
          {errors.district && <p className={err}>{errors.district.message}</p>}
        </div>
        <div>
          <label className={label}>State *</label>
          <input {...register('state')} placeholder="Auto-filled from PIN" className={field}/>
          {errors.state && <p className={err}>{errors.state.message}</p>}
        </div>
      </div>

      {/* Contact */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={label}>Contact Email *</label>
          <input {...register('contactEmail')} type="email" placeholder="admin@cityhospital.in" className={field}/>
          {errors.contactEmail && <p className={err}>{errors.contactEmail.message}</p>}
        </div>
        <div>
          <label className={label}>Mobile *</label>
          <input {...register('contactMobile')} placeholder="9876543210" maxLength={10} className={field}/>
          {errors.contactMobile && <p className={err}>{errors.contactMobile.message}</p>}
        </div>
      </div>

      {/* GSTIN + Reg No */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={label}>GSTIN <span className="text-gray-400 font-normal">(optional)</span></label>
          <input {...register('gstin')} placeholder="27AABCU9603R1ZX" className={`${field} font-mono`}/>
          {errors.gstin && <p className={err}>{errors.gstin.message}</p>}
        </div>
        <div>
          <label className={label}>Reg. No. <span className="text-gray-400 font-normal">(optional)</span></label>
          <input {...register('regNumber')} placeholder="MH-CEA-2024-001234" className={field}/>
        </div>
      </div>

      {/* Bed count + Specialty */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={label}>Bed Count *</label>
          <select {...register('bedCountCategory')} className={field}>
            <option value="5-10">5 – 10 beds</option>
            <option value="11-30">11 – 30 beds</option>
            <option value="31-50">31 – 50 beds</option>
          </select>
        </div>
        <div>
          <label className={label}>Specialty *</label>
          <select {...register('specialtyType')} className={field}>
            {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-clinical-danger text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <button type="submit" disabled={submitting}
        className="w-full py-3 rounded-lg bg-orange text-navy font-semibold text-sm
                   hover:bg-orange-dark transition-colors disabled:opacity-50
                   flex items-center justify-center gap-2">
        {submitting && <Loader2 size={16} className="animate-spin"/>}
        Save & Continue
      </button>
    </form>
  );
}
